import { useContext, useState } from 'react';

import { Box, IconButton, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectHideSidebar,
  selectWorkflow,
  setHideSidebar,
} from 'state/workflowSlice';

import { WorkflowType } from 'appConstants';
import { ExportPopup, SharePopup } from 'features/common/ExportPopups';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import {
  ExportType,
  downloadMapScreenshot,
  useExportFilename,
} from 'features/common/exportUtils';
import { classNames } from 'features/common/utils';
import { MapContext } from 'features/map/MapContext';
import { WORKFLOW_PLAN_NAMES } from 'features/nav_bar/NavBar';

import styles from './TaskBarTopbar.module.css';

const shareBtn = {
  name: 'share-nodes',
  title: 'Share',
};
const exportBtn = {
  name: 'cloud-arrow-down',
  title: 'Export',
};

export default function TaskBarTopbar({
  ignoreHideSidebar = false,
}: {
  ignoreHideSidebar?: boolean;
}) {
  const dispatch = useAppDispatch();
  const workflow = useAppSelector(selectWorkflow);
  const isSidebarHidden = useAppSelector(selectHideSidebar);
  const collapseTopbar = isSidebarHidden && !ignoreHideSidebar;
  const heading = workflow ? WORKFLOW_PLAN_NAMES[workflow] : '';

  const sidebarIcon = !collapseTopbar
    ? {
        name: 'arrow-right-to-line',
        handleClick: () => dispatch(setHideSidebar(true)),
        title: 'Hide',
      }
    : {
        name: 'arrow-left-to-line',
        handleClick: () => dispatch(setHideSidebar(false)),
        title: 'Expand',
      };
  const { title, name, handleClick } = sidebarIcon;

  return (
    <Box
      className={classNames(
        styles.wrapper,
        collapseTopbar && styles.wrapperHidden,
      )}
    >
      <Typography fontWeight={700} variant="h3">
        {heading}
      </Typography>
      <Box className={styles.iconWrapper}>
        <ShareButton />
        <ExportMapButton />
        <IconButton
          key={title}
          className={styles.btn}
          id={styles[title.toLowerCase()]}
          aria-label={title}
          title={title}
          onClick={handleClick}
        >
          <FontAwesomeIcon name={name} />
        </IconButton>
      </Box>
    </Box>
  );
}

export function ShareButton() {
  const [open, setOpen] = useState<boolean>(false);

  const { title, name } = shareBtn;

  return (
    <>
      <IconButton
        disableRipple
        key={title}
        className={styles.btn}
        aria-label={title}
        title={title}
        id={styles[title.toLowerCase()]}
        onClick={() => setOpen(true)}
      >
        <FontAwesomeIcon name={name} />
      </IconButton>
      <SharePopup open={open} setOpen={setOpen} />
    </>
  );
}

function ExportMapButton() {
  const [open, setOpen] = useState<boolean>(false);
  const workflow = useAppSelector(selectWorkflow);
  const filename = useExportFilename(ExportType.Map);
  const { refMap } = useContext(MapContext);

  const enablePopup = workflow !== WorkflowType.Timeline;

  const handleClick = () => {
    if (!enablePopup) {
      downloadMapScreenshot(refMap?.current, filename, false);
    } else setOpen(true);
  };

  const { title, name } = exportBtn;

  return (
    <>
      <IconButton
        disableRipple
        key={title}
        className={styles.btn}
        aria-label={title}
        title={title}
        onClick={handleClick}
        id={styles[title.toLowerCase()]}
      >
        <FontAwesomeIcon name={name} />
      </IconButton>
      {enablePopup && <ExportPopup open={open} setOpen={setOpen} />}
    </>
  );
}
